import { useCallback, useEffect, useRef, useState } from "react";
import { Pause, Play } from "lucide-react";

const REPORT_EVERY_MS = 5000;

const useThrottledReport = (onProgress) => {
  const last = useRef(0);
  return useCallback((position, duration, event = "timeupdate") => {
    const now = Date.now();
    if (event === "timeupdate" && now - last.current < REPORT_EVERY_MS) return;
    last.current = now;
    onProgress?.(position, duration, event);
  }, [onProgress]);
};

const PlaceholderPlayer = ({ video, onProgress }) => {
  const [playing, setPlaying] = useState(false);
  const [pos, setPos] = useState(video.last_position || 0);
  const report = useThrottledReport(onProgress);
  const duration = video.duration_seconds || 90;
  useEffect(() => {
    if (!playing) return undefined;
    const id = setInterval(() => setPos((p) => {
      const next = Math.min(p + 1, duration);
      report(next, duration, next >= duration ? "ended" : "timeupdate");
      if (next >= duration) setPlaying(false);
      return next;
    }), 1000);
    return () => clearInterval(id);
  }, [playing, duration, report]);
  const mmss = (s) => `${Math.floor(s / 60)}:${String(Math.floor(s % 60)).padStart(2, "0")}`;
  return (
    <div className="relative aspect-video w-full overflow-hidden bg-olive-900 text-cream-50" data-testid="placeholder-player">
      <img src="/brand/tree-cream.png" alt="" aria-hidden="true" className="pointer-events-none absolute left-1/2 top-1/2 h-[70%] -translate-x-1/2 -translate-y-1/2 opacity-[0.08]" />
      <div className="relative flex h-full flex-col items-center justify-center px-6 text-center">
        <p className="text-xs font-semibold tracking-wide text-coral-300">Placeholder · video link coming soon</p>
        <p className="mt-2 font-serif text-xl font-semibold sm:text-2xl">{video.title}</p>
        <button type="button" onClick={() => setPlaying((p) => !p)} disabled={pos >= duration} className="mt-6 inline-flex h-16 w-16 items-center justify-center rounded-full bg-cream-50 text-olive-900 transition-transform hover:scale-105 disabled:opacity-50" aria-label={playing ? "Pause" : "Play"} data-testid="placeholder-play">
          {playing ? <Pause className="h-7 w-7" /> : <Play className="ml-1 h-7 w-7" />}
        </button>
        <p className="mt-4 text-sm text-cream-50/80" data-testid="placeholder-time">{mmss(pos)} / {mmss(duration)}</p>
      </div>
      <div className="absolute inset-x-0 bottom-0 h-1.5 bg-cream-50/20"><div className="h-full bg-terracotta-600 transition-[width]" style={{ width: `${(pos / duration) * 100}%` }} /></div>
    </div>
  );
};

const FilePlayer = ({ video, onProgress }) => {
  const report = useThrottledReport(onProgress);
  return (
    <video controls playsInline className="aspect-video w-full bg-black" src={video.embed_url} data-testid="file-player"
      onTimeUpdate={(e) => report(e.target.currentTime, e.target.duration || video.duration_seconds)}
      onEnded={(e) => report(e.target.currentTime, e.target.duration || video.duration_seconds, "ended")}>
      <track kind="captions" />
    </video>
  );
};

const EmbedPlayer = ({ video, onProgress }) => {
  const frame = useRef(null);
  const report = useThrottledReport(onProgress);
  const isVimeo = video.provider === "vimeo";
  const origin = isVimeo ? "https://player.vimeo.com" : "https://www.youtube-nocookie.com";
  const src = isVimeo ? video.embed_url : `${video.embed_url}&origin=${encodeURIComponent(window.location.origin)}`;

  useEffect(() => {
    const post = (msg) => frame.current?.contentWindow?.postMessage(isVimeo ? JSON.stringify(msg) : JSON.stringify(msg), origin);
    const onMessage = (e) => {
      if (e.origin !== origin) return;
      let data = e.data;
      if (typeof data === "string") { try { data = JSON.parse(data); } catch { return; } }
      if (isVimeo) {
        if (data.event === "ready") { post({ method: "addEventListener", value: "timeupdate" }); post({ method: "addEventListener", value: "ended" }); }
        if (data.event === "timeupdate" && data.data) report(data.data.seconds, data.data.duration);
        if (data.event === "ended") report(video.duration_seconds, video.duration_seconds, "ended");
      } else if (data.event === "infoDelivery" && data.info) {
        if (typeof data.info.currentTime === "number") report(data.info.currentTime, data.info.duration || video.duration_seconds, data.info.playerState === 0 ? "ended" : "timeupdate");
      }
    };
    window.addEventListener("message", onMessage);
    const el = frame.current;
    const onLoad = () => { if (!isVimeo) post({ event: "listening", id: 1, channel: "widget" }); };
    el?.addEventListener("load", onLoad);
    return () => { window.removeEventListener("message", onMessage); el?.removeEventListener("load", onLoad); };
  }, [isVimeo, origin, report, video.duration_seconds]);

  return <iframe ref={frame} src={src} title={video.title} className="aspect-video w-full bg-black" allow="autoplay; fullscreen; picture-in-picture" allowFullScreen referrerPolicy="strict-origin-when-cross-origin" data-testid="embed-player" />;
};

export const VideoPlayer = ({ video, onProgress }) => {
  if (!video) return null;
  if (video.provider === "placeholder" || !video.embed_url) return <PlaceholderPlayer key={video.id} video={video} onProgress={onProgress} />;
  if (video.provider === "file") return <FilePlayer key={video.id} video={video} onProgress={onProgress} />;
  return <EmbedPlayer key={video.id} video={video} onProgress={onProgress} />;
};
